import { z } from 'zod'
import { asObjectType, asEnumType } from '@gqloom/zod'

// Enums
export const ReleaseNoteStatus = {
  Draft: 'DRAFT',
  Published: 'PUBLISHED',
} as const

export const ReleaseNoteType = {
  Feature: 'FEATURE',
  Enhancement: 'ENHANCEMENT',
  BugFix: 'BUG_FIX',
  BreakingChange: 'BREAKING_CHANGE',
} as const

export const ReleaseNoteTranslationStatus = {
  Pending: 'PENDING',
  Translated: 'TRANSLATED',
  Failed: 'FAILED',
} as const

// Type exports for enums
export type ReleaseNoteStatusValue = (typeof ReleaseNoteStatus)[keyof typeof ReleaseNoteStatus]
export type ReleaseNoteTypeValue = (typeof ReleaseNoteType)[keyof typeof ReleaseNoteType]
export type ReleaseNoteTranslationStatusValue =
  (typeof ReleaseNoteTranslationStatus)[keyof typeof ReleaseNoteTranslationStatus]

// Release Note schema
export const releaseNoteSchema = z
  .object({
    releaseNoteId: z.string().uuid(),
    slug: z.string().min(1).max(255),
    title: z.string().min(1).max(255),
    version: z.string().max(50).nullable().optional(),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
    releaseType: z.nativeEnum(ReleaseNoteType).register(asEnumType, {
      name: 'ReleaseNoteType',
    }),
    tags: z.array(z.string().max(100)).nullable().optional(),
    tierTags: z.array(z.string().max(50)).nullable().optional(),
    roleTags: z.array(z.string().max(50)).nullable().optional(),
    permissionTags: z.array(z.string().max(100)).nullable().optional(),
    status: z.nativeEnum(ReleaseNoteStatus).register(asEnumType, {
      name: 'ReleaseNoteStatus',
    }),
    publishedAt: z.date().nullable().optional(),
    createdBy: z.string().uuid(),
    updatedBy: z.string().uuid().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
    deletedAt: z.date().nullable().optional(),
  })
  .register(asObjectType, {
    name: 'ReleaseNote',
    description: 'A release note or changelog entry',
  })

// Create schema - excludes system fields
export const releaseNoteCreateSchema = z
  .object({
    slug: z.string().min(1).max(255),
    title: z.string().min(1).max(255),
    version: z.string().max(50).nullable().optional(),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
    releaseType: z.nativeEnum(ReleaseNoteType),
    tags: z.array(z.string().max(100)).nullable().optional(),
    tierTags: z.array(z.string().max(50)).nullable().optional(),
    roleTags: z.array(z.string().max(50)).nullable().optional(),
    permissionTags: z.array(z.string().max(100)).nullable().optional(),
    status: z.nativeEnum(ReleaseNoteStatus).optional().default(ReleaseNoteStatus.Draft),
  })
  .register(asObjectType, {
    name: 'ReleaseNoteCreateInput',
    description: 'Input for creating a release note',
  })

// Update schema - all content fields optional
export const releaseNoteUpdateSchema = z
  .object({
    slug: z.string().min(1).max(255).optional(),
    title: z.string().min(1).max(255).optional(),
    version: z.string().max(50).nullable().optional(),
    contentMarkdown: z.string().min(1).optional(),
    summary: z.string().max(1000).nullable().optional(),
    releaseType: z.nativeEnum(ReleaseNoteType).optional(),
    tags: z.array(z.string().max(100)).nullable().optional(),
    tierTags: z.array(z.string().max(50)).nullable().optional(),
    roleTags: z.array(z.string().max(50)).nullable().optional(),
    permissionTags: z.array(z.string().max(100)).nullable().optional(),
    status: z.nativeEnum(ReleaseNoteStatus).optional(),
  })
  .register(asObjectType, {
    name: 'ReleaseNoteUpdateInput',
    description: 'Input for updating a release note',
  })

// Filter schema for queries
export const releaseNoteFilterSchema = z
  .object({
    status: z.nativeEnum(ReleaseNoteStatus).optional(),
    releaseType: z.nativeEnum(ReleaseNoteType).optional(),
    tags: z.array(z.string()).optional(),
    tierTags: z.array(z.string()).optional(),
    roleTags: z.array(z.string()).optional(),
    permissionTags: z.array(z.string()).optional(),
    search: z.string().optional(),
    includeDeleted: z.boolean().optional().default(false),
  })
  .register(asObjectType, {
    name: 'ReleaseNoteFilterInput',
    description: 'Filter options for querying release notes',
  })

// Release Note Translation schema
export const releaseNoteTranslationSchema = z
  .object({
    translationId: z.string().uuid(),
    releaseNoteId: z.string().uuid(),
    locale: z.string().min(2).max(10),
    title: z.string().min(1).max(255),
    contentMarkdown: z.string().min(1),
    summary: z.string().max(1000).nullable().optional(),
    status: z.nativeEnum(ReleaseNoteTranslationStatus).register(asEnumType, {
      name: 'ReleaseNoteTranslationStatus',
    }),
    translatedAt: z.date().nullable().optional(),
    createdAt: z.date(),
    updatedAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ReleaseNoteTranslation',
    description: 'A translated version of a release note',
  })

// Release Note Read Status schema (for tracking read state per user)
export const releaseNoteReadStatusSchema = z
  .object({
    userId: z.string().uuid(),
    releaseNoteId: z.string().uuid(),
    readAt: z.date(),
  })
  .register(asObjectType, {
    name: 'ReleaseNoteReadStatus',
    description: 'Tracks which users have read which release notes',
  })

// Type exports
export type ReleaseNote = z.infer<typeof releaseNoteSchema>
export type ReleaseNoteCreate = z.infer<typeof releaseNoteCreateSchema>
export type ReleaseNoteUpdate = z.infer<typeof releaseNoteUpdateSchema>
export type ReleaseNoteFilter = z.infer<typeof releaseNoteFilterSchema>
export type ReleaseNoteTranslation = z.infer<typeof releaseNoteTranslationSchema>
export type ReleaseNoteReadStatus = z.infer<typeof releaseNoteReadStatusSchema>
